import i18next from 'i18next';
import { BUILD_ID } from '../buildInfo';

/*
 * What to do when the server answers 426: "this app's version is not mine".
 *
 * ────────────────────────────────────────────────────────────────────────
 * WHY THE PAGE USED TO RELOAD FOREVER
 * ────────────────────────────────────────────────────────────────────────
 *
 * mushroom-bank/lambda/handler.py refuses every POST/PUT/DELETE whose
 * X-App-Version differs from the Lambda's APP_VERSION. A 426 does not say
 * WHICH side is behind, and api.js answered every one with an alert and a
 * reload. That fixes an OLD page talking to a NEW server: the reload fetches
 * the new index.html (the service worker serves HTML network-first). It can
 * never fix a NEW page talking to an OLD server -- the reload brings back the
 * same newest bundle -- and that is exactly what every merge to main produces:
 * Amplify publishes the frontend in ~3 minutes, the gated Lambda deploy takes
 * ~13 and queues behind the previous one (2026-10-07: ~23 minutes). Any page
 * that writes on load (Field Mode's and the map's outbox, /sync, a sign-in)
 * then alerted and reloaded until the Lambda caught up, or forever if its
 * deploy failed.
 *
 * ────────────────────────────────────────────────────────────────────────
 * WHAT HAPPENS NOW
 * ────────────────────────────────────────────────────────────────────────
 *
 * The page asks which build is DEPLOYED: /service-worker.js is stamped with it
 * at build time (scripts/stamp-service-worker.js, the same SHA as buildInfo.js),
 * and the worker never serves that file from a cache (NEVER_CACHE_EXACT).
 *
 *   * deployed === this page's build: this page is the newest; the server is
 *     mid-deploy. Nothing reloads. The refused request's error says so, in the
 *     reader's language, and that nothing was saved.
 *   * deployed differs: this page is out of date. Reload -- at most once per
 *     (from -> to) pair per PAIR_RETRY_MS and at most MAX_AUTO_RELOADS times per
 *     GUARD_TTL_MS per tab, and only after that is written to sessionStorage
 *     AND read back. So even CDN edges that disagree (A says B, B says A) stop
 *     after two reloads in half an hour, while a reload that came back to the
 *     same bundle (CDN lag) may try once more a few minutes later.
 *   * unknown (no answer, an unstamped dev worker, storage refused): never a
 *     blind reload. The error asks the reader to refresh in a few minutes.
 *
 * One decision serves a burst of 426s (one probe, one alert, one reload), and
 * a decision not to reload is reused for RECHECK_MS, so an outbox with ten
 * batches does not fetch the worker ten times.
 *
 * Deliberately NOT done: SKIP_WAITING / controllerchange. A reload already
 * gets the new HTML through the old worker, and activating the new worker
 * prunes the shared bundle cache and claims every open tab -- other tabs still
 * on the old build would lose their lazy chunks.
 */

export const GUARD_KEY = 'mb_version_reload';
export const GUARD_TTL_MS = 30 * 60 * 1000;
export const MAX_AUTO_RELOADS = 2;
// A reload that came back to the same bundle (CDN lag, a flaky link) may try
// that pair once more after this; MAX_AUTO_RELOADS still bounds the total.
export const PAIR_RETRY_MS = 5 * 60 * 1000;
export const PROBE_TIMEOUT_MS = 5000;
export const RECHECK_MS = 20 * 1000;
export const PROBE_PATH = `${process.env.PUBLIC_URL || ''}/service-worker.js`;

const STAMP = /var BUILD_ID = '([^']*)'/;
const SHA = /^[0-9a-f]{7,40}$/i;

/** A build id two builds can be compared by: a commit SHA. Not 'dev', 'dev-<ms>' or the unstamped placeholder. */
export const comparable = id => typeof id === 'string' && SHA.test(id);

/** The build id stamped into a service worker's source, or null. */
export function parseStamp(text) {
  const m = STAMP.exec(String(text || ''));
  return m && comparable(m[1]) ? m[1] : null;
}

/** The build the server is publishing now, or null when it cannot be told. Never throws. */
export async function deployedBuild({ fetchImpl = (...a) => globalThis.fetch(...a), timeoutMs = PROBE_TIMEOUT_MS } = {}) {
  const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timer = ctrl ? setTimeout(() => ctrl.abort(), timeoutMs) : null;
  try {
    // Plain fetch, never the axios client: the probe must not pass through the
    // interceptor it is answering for.
    const res = await fetchImpl(PROBE_PATH, { cache: 'no-store', ...(ctrl ? { signal: ctrl.signal } : {}) });
    return res && res.ok ? parseStamp(await res.text()) : null;
  } catch {
    return null;
  } finally {
    if (timer) clearTimeout(timer);
  }
}

/**
 * Record an automatic reload from `from` to `to`, if one is still allowed:
 * the pair not spent within PAIR_RETRY_MS, and fewer than MAX_AUTO_RELOADS
 * reloads within GUARD_TTL_MS. True only when the record was written AND reads back --
 * storage that cannot keep it means no automatic reload, ever.
 */
export function spendReload(getStorage, from, to, now) {
  try {
    const storage = getStorage();
    let saved = null;
    try { saved = JSON.parse(storage.getItem(GUARD_KEY) || 'null'); } catch { saved = null; }
    const within = ms => at => Number.isFinite(at) && now - at < ms;
    const pairs = Object.fromEntries(Object.entries((saved && typeof saved.pairs === 'object' && saved.pairs) || {})
      .filter(([, at]) => within(PAIR_RETRY_MS)(at)));
    const reloads = (Array.isArray(saved?.reloads) ? saved.reloads : []).filter(within(GUARD_TTL_MS));
    const pair = `${from}>${to}`;
    if (pairs[pair] || reloads.length >= MAX_AUTO_RELOADS) return false;
    const next = JSON.stringify({ pairs: { ...pairs, [pair]: now }, reloads: [...reloads, now] });
    storage.setItem(GUARD_KEY, next);
    return storage.getItem(GUARD_KEY) === next;
  } catch {
    return false;
  }
}

const EN = {
  reloading: 'A new version of PITRIOT is available. The page will now reload.',
  serverUpdating: 'PITRIOT is being updated and the server is not ready for this version yet. Nothing was saved. Try again in a few minutes.',
  refresh: 'A newer version of PITRIOT is available but this page could not load it. Nothing was saved. Refresh the page and try again.',
  mismatch: 'The app and the server are on different versions, probably because an update is in progress. Nothing was saved. Wait a few minutes, refresh the page and try again.',
};

// Literal keys, so keysExist.test.js holds them to the catalogue. t() before
// i18next is initialised answers undefined: English then.
const MESSAGES = {
  reloading: () => i18next.t('common:appVersion.reloading', { defaultValue: EN.reloading }) || EN.reloading,
  serverUpdating: () => i18next.t('common:appVersion.serverUpdating', { defaultValue: EN.serverUpdating }) || EN.serverUpdating,
  refresh: () => i18next.t('common:appVersion.refresh', { defaultValue: EN.refresh }) || EN.refresh,
  mismatch: () => i18next.t('common:appVersion.mismatch', { defaultValue: EN.mismatch }) || EN.mismatch,
};

export function createVersionSkew({
  running = BUILD_ID,
  fetchImpl,
  getStorage = () => window.sessionStorage,
  alert = m => window.alert(m),
  reload = () => window.location.reload(),
  now = () => Date.now(),
  messages = MESSAGES,
} = {}) {
  let deciding = null;
  let reloading = false;
  let last = null; // { kind, at }: a decision not to reload, reused for RECHECK_MS

  async function decide() {
    const deployed = await deployedBuild(fetchImpl ? { fetchImpl } : {});
    // This page IS the newest build: the server is behind. A reload cannot help.
    if (deployed && deployed === running) return { reload: false, kind: 'serverUpdating' };
    // Cannot tell: never reload blind.
    if (!deployed || !comparable(running)) return { reload: false, kind: 'mismatch' };
    if (spendReload(getStorage, running, deployed, now())) return { reload: true };
    return { reload: false, kind: 'refresh' };
  }

  function explain(error, kind) {
    if (!error?.response) return;
    const data = error.response.data;
    error.response.data = {
      ...(data && typeof data === 'object' ? data : {}),
      error: messages[kind](),
      server_error: data && typeof data === 'object' ? data.error : data,
    };
  }

  async function answer(error) {
    if (reloading) return;
    if (last && now() - last.at < RECHECK_MS) {
      explain(error, last.kind);
      return;
    }
    deciding = deciding || decide().finally(() => { deciding = null; });
    const verdict = await deciding;
    if (reloading) return;
    if (verdict.reload) {
      reloading = true;
      alert(messages.reloading());
      reload();
      return;
    }
    last = { kind: verdict.kind, at: now() };
    explain(error, verdict.kind);
  }

  return { answer };
}

let instance = null;
/** api.js's answer to a web 426: reload when a newer app is published (bounded), else explain. */
export function answerOutdated(error) {
  instance = instance || createVersionSkew();
  return instance.answer(error);
}
